import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from '../components/DelegationBadge';
import {
    providerColor,
    providerLabel,
} from '../providerLabels';
import config from '../config';

// ⚠️ PROVIDER IS A COLUMN, NOT A GROUPING.
//
// A portfolio spread across three providers is ONE list with provenance as an
// attribute — not three tables, not a grouped table, not a provider switcher.
// That is the point of the neutral layer: `provider` is the only field in the
// schema that reveals where a zone came from, and it earns a column rather than
// restructuring the page.
export const useZonesTableColumns = () => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<DnsZone>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            width: '2fr',
            // ⚠️ THIS WIDTH IS INERT. IT IS NOT DOING WHAT IT LOOKS LIKE.
            //
            // `mantine-datatable@7.17.1` applies a column width as an INLINE
            // STYLE on the cell (`c.width ?? "initial"` in its dist bundle).
            // `2fr` is a CSS Grid unit, invalid on a table cell, so the browser
            // discards it. Removing it changes nothing; adding it changes
            // nothing.
            //
            // It is left in place only because certificates carries the same
            // thing and is the module that renders correctly, so removing it
            // here would create a difference that is not the real one. It is
            // not a fix and must not be cited as one.
            //
            // ⚠️ FNP-1284 GOT THIS WRONG FOUR TIMES, each from reading source
            // rather than measuring the DOM:
            //   1. "`1fr` is invalid on a table cell"       - true, but so is
            //      `2fr`, which the CLEAN module uses. Never the discriminator.
            //   2. "the fixed columns overflow"             - certificates has
            //      620px against this module's 530px, and is clean.
            //   3. "`nameservers` has no width"             - it has 110px.
            //   4. "restoring the fraction fixes it"        - it did not. The
            //      owner confirmed the scrollbar survives, with AND without
            //      rows, which also kills every empty-table theory.
            //
            // The cause is STILL UNKNOWN. Do not add a fifth theory from
            // reading this file. Measure it: on /zones in DevTools, filter
            // every element for `scrollWidth > clientWidth` and the overflowing
            // node names itself.
            //
            // Live hypothesis, unproven: `ConnectionFailuresAlert` is rendered
            // by this module alone and prints unbroken ARNs and connection ids
            // with no `wordBreak`. Against it: Mantine's Alert sets
            // `overflow: hidden`, and the scrollbar appears whether or not a
            // connection is failing.
            render: (item: DnsZone) => (
                // ⚠️ The link carries `connectionId`, because `/zones/:zoneId`
                // alone cannot address a zone — getDnsZone needs both. See
                // hooks/useZone for the full resolution order.
                <NavLink
                    to={`${config.basePath}/${encodeURIComponent(item.id)}?connectionId=${encodeURIComponent(item.connectionId)}`}
                >
                    <Text size={'sm'}>{item.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'provider',
            sortable: true,
            title: I18n.get('Provider'),
            width: '140px',
            render: (item: DnsZone) => (
                <Badge variant={'light'} color={providerColor(item.provider)}>
                    {providerLabel(item.provider)}
                </Badge>
            ),
        },
        {
            accessor: 'appearsDelegated',
            sortable: true,
            title: I18n.get('Delegation'),
            width: '160px',
            render: (item: DnsZone) => <DelegationBadge zone={item}/>,
        },
        {
            accessor: 'isPrivate',
            sortable: true,
            title: I18n.get('Visibility'),
            width: '120px',
            // `isPrivate` is `Boolean!` — non-null, so unlike the hosted-zones
            // equivalent there is no third state to render here.
            render: (item: DnsZone) => (
                <Badge
                    variant={'light'}
                    color={item.isPrivate ? 'orange' : 'blue'}
                >
                    {item.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
        },
        {
            accessor: 'nameservers',
            sortable: true,
            title: I18n.get('Nameservers'),
            width: '110px',
            // The COUNT, not the list: four hostnames per row would drown the
            // table. The full delegation set is on the detail page.
            render: (item: DnsZone) => (
                <Text size={'sm'}>{item.nameservers?.length ?? 0}</Text>
            ),
        },
        // ⚠️ THERE IS DELIBERATELY NO ZONE ID COLUMN.
        //
        // `DnsZone.id` is OPAQUE — Flexi's own identifier, minted by the
        // adapter, never the provider's. Putting it in front of an operator
        // invites them to paste it into the AWS console or a Cloudflare
        // dashboard, where it means nothing. The hosted-zones module shows its
        // zone id precisely because that one IS the Route 53 id.
    ];

    return {
        columns,
    };
};
